/**
 * WorkBuddy 客户端内嵌打开业务页时的静默登录链路。
 *
 * 只负责「已授权且已绑定」这条快路径：直接用 wb.login 的授权码换取 comein 会话并补齐
 * BRM 登录态，用户无感进入业务页。确实需要用户确认的场景（未授权、未绑定 comein 账号）
 * 才交给授权页，非 WorkBuddy 环境则交回通用未登录处理。
 *
 * 每次整页加载都会强制走一遍本链路（即使本地已有登录态），保证 BRM 会话始终对应
 * WorkBuddy 当前账号，避免沿用本机残留的其它账号登录态。
 */
import store from '@/store';
import { LOCAL_STORAGE_KEY } from '@/constants';
import { checkLogin, storage } from '@/utils/storageOperateApi';
import { loadWorkBuddyJsApi, requestWorkBuddyAuthorizationCode } from '@/utils/workBuddyJsApi';
import { loginWorkBuddyBinding, WORKBUDDY_PLATFORM_CODE } from '@/jmHttp/api/thirdPartyOauth';
import slsLogger from '@/utils/slsLogger';

/** slsLogger 组件名 */
const LOG_COMPONENT = 'WorkBuddySilentLogin';

/** 静默登录链路的结束状态，由路由守卫据此决定后续跳转 */
export const WORKBUDDY_LOGIN_STATUS = {
  /** 已补齐 BRM 登录态，可直接进入业务页 */
  LOGGED_IN: 'loggedIn',
  /** 需要用户在授权页确认授权或完成账号绑定 */
  NEED_AUTHORIZE: 'needAuthorize',
  /** 非 WorkBuddy 环境或链路异常，交回通用未登录处理 */
  UNAVAILABLE: 'unavailable'
};

/**
 * 单次静默换取登录态的结果。
 *
 * 比 WORKBUDDY_LOGIN_STATUS 更细，因为登录态失效后的轮询恢复要据此判断「继续等」还是
 * 「必须由用户介入」：未绑定只是对侧还没绑完，重试就能等到；取不到授权码则说明客户端侧
 * 已撤销授权，再怎么重试也拿不到 code。
 */
export const WORKBUDDY_SESSION_EXCHANGE = {
  /** 已补齐 BRM 登录态 */
  LOGGED_IN: 'loggedIn',
  /** 取不到 JSAPI：非 WorkBuddy 客户端环境 */
  NO_JSAPI: 'noJsApi',
  /** 取不到授权码：客户端侧未授权或用户取消 */
  NO_CODE: 'noCode',
  /** WorkBuddy 账号未绑定 comein 账号 */
  NOT_BOUND: 'notBound',
  /** binding/login 调用失败（网络或业务错误） */
  REQUEST_FAILED: 'requestFailed',
  /** 已绑定但换不出完整 BRM 登录态，如账号未加入任何机构 */
  SESSION_INCOMPLETE: 'sessionIncomplete'
};

/**
 * 是否已尝试过且未能登录成功。
 * 换取失败时守卫会继续走通用登录，若不做标记，登录页与业务页之间的来回跳转会反复唤起
 * wb.login。成功后重置，以便会话过期时还能再次静默续上；整页刷新同样重置。
 */
let hasFailedAttempt = false;

/**
 * 本次整页加载内是否已走过一遍 wb.login 链路。
 * WorkBuddy 打开的页面即使本地已有登录态也要强制校验一次，但 SPA 站内跳转不重复唤起；
 * 整页刷新后重置，下次打开再次强制校验。
 */
let hasAttemptedThisPageLoad = false;

/**
 * 本次整页加载是否还需要强制走一遍 wb.login，供路由守卫在已登录态下判断。
 * @returns {boolean} 尚未走过链路时返回 true
 */
export function shouldForceWorkBuddyLogin() {
  return !hasAttemptedThisPageLoad;
}

/**
 * 用 comein 会话 token 补齐 BRM 登录态。
 *
 * binding/login 只下发 comein 会话，不含机构信息，因此先用 token 反查用户信息拿到
 * organizationId，再换取 BRM token 与菜单，与守卫里 MAIN_TOKEN 无感登录同一套流程。
 * @param {string} comeinToken - binding/login 下发的 comein 会话 token
 * @returns {Promise<boolean>} 是否已具备完整 BRM 登录态
 * @example
 * if (bindingInfo.bound && (await exchangeBrmSession(bindingInfo.token))) router.replace(redirectUri);
 */
export async function exchangeBrmSession(comeinToken) {
  const userInfoRes = await store.dispatch('jm/getUserInfoByToken', { token: comeinToken });
  const jmAuthInfo = userInfoRes?.data;
  const { organizationId, webtoken, uid } = jmAuthInfo || {};
  // 未加入任何机构时拿不到 organizationId，无法换取 BRM 菜单，需引导用户走登录页选机构
  if (!organizationId || !webtoken) {
    slsLogger.console({
      component: LOG_COMPONENT,
      level: 'warn',
      message: 'WorkBuddy 静默登录-comein 会话缺少机构信息',
      extra: { hasOrganizationId: !!organizationId, hasWebtoken: !!webtoken }
    });
    return false;
  }

  await store.dispatch('fetchUserTokenAndMenuAction', { goId: organizationId, token: webtoken, uid });
  // fetchUserTokenAndMenuAction 内部会清空 localStorage，JM 登录态必须在其之后再写回
  storage.set(LOCAL_STORAGE_KEY.JM_AUTH_INFO, jmAuthInfo, '', true);
  return checkLogin();
}

/**
 * 用 wb.login 授权码换一次 comein 会话并补齐 BRM 登录态。
 *
 * 不带任何一次性锁与页面级状态，可被首屏静默登录与登录态失效后的轮询恢复共用；调用方按
 * 返回的结果自行决定是重试还是转授权页。
 * @param {string} logComponent - slsLogger 组件名，用于区分调用来源
 * @returns {Promise<string>} WORKBUDDY_SESSION_EXCHANGE 中的一项
 * @example
 * const result = await exchangeWorkBuddySessionSilently('WorkBuddySessionRecovery');
 * if (result === WORKBUDDY_SESSION_EXCHANGE.LOGGED_IN) window.location.reload();
 */
export async function exchangeWorkBuddySessionSilently(logComponent) {
  let hasLoginApi = false;
  try {
    hasLoginApi = await loadWorkBuddyJsApi();
  } catch (error) {
    slsLogger.console({
      component: logComponent,
      level: 'error',
      message: 'WorkBuddy 静默登录-JSAPI 加载失败',
      extra: { errorMessage: error?.message || 'Unknown error' }
    });
    return WORKBUDDY_SESSION_EXCHANGE.NO_JSAPI;
  }
  // 带着参数在普通浏览器里打开时取不到 JSAPI
  if (!hasLoginApi) return WORKBUDDY_SESSION_EXCHANGE.NO_JSAPI;

  const code = await requestWorkBuddyAuthorizationCode(logComponent);
  // 未授权或用户取消：只能由用户在授权页重新确认授权
  if (!code) return WORKBUDDY_SESSION_EXCHANGE.NO_CODE;

  let bindingInfo;
  slsLogger.console({
    component: logComponent,
    message: 'WorkBuddy 静默登录-请求换取 comein 会话',
    extra: { platformCode: WORKBUDDY_PLATFORM_CODE, code }
  });
  try {
    const response = (await loginWorkBuddyBinding(code)) || {};
    // 全量上报返回，业务错误码与正常返回都能在 SLS 里直接核对
    slsLogger.console({
      component: logComponent,
      message: 'WorkBuddy 静默登录-换取 comein 会话返回',
      extra: response
    });
    if (typeof response.code !== 'undefined' && String(response.code) !== '0') {
      throw new Error(response.errordesc || response.msg || 'WorkBuddy 授权码换取 comein 会话失败');
    }
    bindingInfo = response.data || {};
  } catch (error) {
    slsLogger.console({
      component: logComponent,
      level: 'error',
      message: 'WorkBuddy 静默登录-换取 comein 会话失败',
      // jmHttp 失败时抛出的是响应体而非 Error，故错误码与描述字段都要带上
      extra: {
        code: error?.code,
        errorcode: error?.errorcode,
        errordesc: error?.errordesc,
        msg: error?.msg,
        errorMessage: error?.message
      }
    });
    return WORKBUDDY_SESSION_EXCHANGE.REQUEST_FAILED;
  }

  // 未绑定 comein 账号：本次 code 已被消费，需由授权页取新 code 换绑定票据后引导绑定
  if (!bindingInfo.bound || !bindingInfo.token) {
    slsLogger.console({
      component: logComponent,
      message: 'WorkBuddy 静默登录-账号未绑定 comein 账号',
      extra: { bound: bindingInfo.bound }
    });
    return WORKBUDDY_SESSION_EXCHANGE.NOT_BOUND;
  }

  try {
    if (await exchangeBrmSession(bindingInfo.token)) {
      slsLogger.console({
        component: logComponent,
        message: 'WorkBuddy 静默登录-登录成功',
        extra: { uid: bindingInfo.uid, expireAt: bindingInfo.expireAt }
      });
      return WORKBUDDY_SESSION_EXCHANGE.LOGGED_IN;
    }
  } catch (error) {
    slsLogger.console({
      component: logComponent,
      level: 'error',
      message: 'WorkBuddy 静默登录-补齐 BRM 登录态失败',
      extra: { uid: bindingInfo.uid, errorMessage: error?.message || 'Unknown error' }
    });
  }
  // 已绑定但换不出 BRM 登录态（如未加入机构）：需去登录页选机构
  return WORKBUDDY_SESSION_EXCHANGE.SESSION_INCOMPLETE;
}

/**
 * 尝试用 WorkBuddy 授权码静默登录。
 * @returns {Promise<string>} WORKBUDDY_LOGIN_STATUS 中的一项
 * @example
 * const status = await silentLoginFromWorkBuddy();
 * if (status === WORKBUDDY_LOGIN_STATUS.LOGGED_IN) next(to.fullPath);
 */
export async function silentLoginFromWorkBuddy() {
  hasAttemptedThisPageLoad = true;
  if (hasFailedAttempt) return WORKBUDDY_LOGIN_STATUS.UNAVAILABLE;
  hasFailedAttempt = true;

  const result = await exchangeWorkBuddySessionSilently(LOG_COMPONENT);
  if (result === WORKBUDDY_SESSION_EXCHANGE.LOGGED_IN) {
    hasFailedAttempt = false;
    return WORKBUDDY_LOGIN_STATUS.LOGGED_IN;
  }
  // 非 WorkBuddy 环境或接口调用失败：授权页同样推进不了，交回通用登录处理
  if (result === WORKBUDDY_SESSION_EXCHANGE.NO_JSAPI || result === WORKBUDDY_SESSION_EXCHANGE.REQUEST_FAILED) {
    return WORKBUDDY_LOGIN_STATUS.UNAVAILABLE;
  }
  // 未授权 / 未绑定 / 缺机构信息：授权页能引导用户补齐
  return WORKBUDDY_LOGIN_STATUS.NEED_AUTHORIZE;
}
